import { useEffect, useMemo, useState, Fragment, type ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate } from "react-router-dom";
import { FiEdit2, FiRefreshCw, FiSave, FiX } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchAllEmployees } from "../../store/allocation/employeeSlice";
import {
  fetchMarkedUnitsCases,
  fetchRecentlyAssignedUnits,
  updateMarkedUnits,
} from "../../store/superAdmin/markedUnitsSlice";
import EmployeeNameEditor, {
  ModelPersonRow,
  WrappingSelect,
} from "../../components/admin/EmployeeNameEditor";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminSearchInput,
  AdminSelect,
  AdminPagination,
  AdminTableCard,
  AdminTableState,
  AdminPrimaryButton,
} from "../../components/admin/AdminPageShell";
import {
  InternalRedesignsUnitsPanel,
  JobCardNotesModal,
  LiveCaseUnitsHint,
  OriginalValue,
  RedesignScopeBadges,
  RedesignScopeEditor,
  applyClientRedesignScopeChange,
  categoryAccent,
  categoryRowClass,
  defaultInternalAssignments,
  parseUnitsRedesignLabels,
  serializeInternalAssignments,
  formatPeopleUnits,
  qcCreditForPerson,
  qcUnitsFromAssignedWork,
  sameText,
  splitNames,
  sumUnits,
  unitsTableInputClass,
  unitsTableTd,
  unitsTableTh,
  type InternalRedesignAssignment,
  type UnitsNotesModalState,
  type UnitsRedesignScope,
} from "../../components/admin/unitsTableShared";
import ToothNumberPicker, {
  ToothChips,
} from "../../components/admin/ToothNumberPicker";
import UnitsModelEditor from "../../components/admin/UnitsModelEditor";
import {
  BILLING_CATEGORIES,
  UNITS_CRM_ROLES,
  UNITS_QC2_ROLES,
  formatModelTypes,
  hasModelCategorySelection,
  hasModelWorkSelection,
  modelCategoryWorkUnits,
  modelDesignerUnits,
  modelOtherItemWorkUnits,
  parseDesignerWork,
  parseModelTypes,
  persistModelTypes,
  suggestBillingCategory,
} from "../../utils/jobCard";

type TypeFilter = "" | "live" | "redesign";
export type MarkedUnitsMode = "marked" | "recent";

const LIMIT = 25;
const RECENT_LIMIT = 50;

const getRowKey = (row: any) =>
  `${row.type}-${row.case_id}-${row.details || ""}`;

const ReadValue = ({ children }: { children?: ReactNode }) => {
  const text = children == null || children === "" ? "—" : children;
  return (
    <div className="text-[14px] leading-snug break-words text-[#4A4A48]">{text}</div>
  );
};

export const MarkedUnitsPage = ({
  mode = "marked",
}: {
  mode?: MarkedUnitsMode;
}) => {
  const isRecent = mode === "recent";
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const { allEmployees } = useAppSelector((state) => state.employee);

  const {
    cases,
    totalCount,
    currentPage,
    totalPages,
    loading,
  } = useAppSelector((state) => state.markedUnits);

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("");
  const [saving, setSaving] = useState(false);

  const [editedDeliveryUnits, setEditedDeliveryUnits] = useState<
    Record<string, number>
  >({});
  const [editedDesigners, setEditedDesigners] = useState<Record<string, string>>(
    {},
  );
  const [editedDesignerUnits, setEditedDesignerUnits] = useState<
    Record<string, Record<string, number>>
  >({});
  const [editedDesignerWork, setEditedDesignerWork] = useState<
    Record<string, Record<string, "normal" | "model">>
  >({});
  const [editedQc2, setEditedQc2] = useState<Record<string, string>>({});
  const [editedDesignTn, setEditedDesignTn] = useState<Record<string, string>>(
    {},
  );
  const [editedDeliveryTn, setEditedDeliveryTn] = useState<
    Record<string, string>
  >({});
  const [editedCategory, setEditedCategory] = useState<Record<string, string>>(
    {},
  );
  const [categoryEdited, setCategoryEdited] = useState<Record<string, boolean>>(
    {},
  );
  const [editedFilesBy, setEditedFilesBy] = useState<Record<string, string>>(
    {},
  );
  const [editedCrm, setEditedCrm] = useState<Record<string, string>>({});
  const [editedModelType, setEditedModelType] = useState<
    Record<string, string[]>
  >({});
  const [editedModelBy, setEditedModelBy] = useState<Record<string, string>>(
    {},
  );
  const [editedModelUnits, setEditedModelUnits] = useState<
    Record<string, number>
  >({});
  const [editedUnitsNotes, setEditedUnitsNotes] = useState<
    Record<string, string>
  >({});
  const [editedIrAssignments, setEditedIrAssignments] = useState<
    Record<string, InternalRedesignAssignment[]>
  >({});
  const [editedRedesignCategory, setEditedRedesignCategory] = useState<
    Record<string, string>
  >({});
  const [editingRows, setEditingRows] = useState<Record<string, boolean>>({});
  const [notesModal, setNotesModal] = useState<UnitsNotesModalState | null>(
    null,
  );

  const designersList = useMemo(
    () =>
      allEmployees?.filter(
        (emp) =>
          emp.verified &&
          ["Designer", "Senior Designer", "Captain", "Assistant Captain", "Super Admin"].includes(
            emp.role,
          ),
      ) ?? [],
    [allEmployees],
  );

  const qc2List = useMemo(
    () =>
      allEmployees?.filter(
        (emp) =>
          emp.verified &&
          UNITS_QC2_ROLES.includes(
            emp.role as (typeof UNITS_QC2_ROLES)[number],
          ),
      ) ?? [],
    [allEmployees],
  );

  const crmList = useMemo(
    () =>
      allEmployees?.filter(
        (emp) =>
          emp.verified &&
          UNITS_CRM_ROLES.includes(
            emp.role as (typeof UNITS_CRM_ROLES)[number],
          ),
      ) ?? [],
    [allEmployees],
  );

  const resetEdits = () => {
    setEditingRows({});
    setEditedDeliveryUnits({});
    setEditedDesigners({});
    setEditedDesignerUnits({});
    setEditedDesignerWork({});
    setEditedQc2({});
    setEditedDesignTn({});
    setEditedDeliveryTn({});
    setEditedCategory({});
    setCategoryEdited({});
    setEditedFilesBy({});
    setEditedCrm({});
    setEditedModelType({});
    setEditedModelBy({});
    setEditedModelUnits({});
    setEditedUnitsNotes({});
    setEditedIrAssignments({});
    setEditedRedesignCategory({});
  };

  const revertField = (
    setter: (updater: (prev: Record<string, any>) => Record<string, any>) => void,
    rowKey: string,
  ) => {
    setter((prev) => {
      if (!(rowKey in prev)) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  const cancelEditing = (rowKey: string) => {
    revertField(setEditedDeliveryUnits, rowKey);
    revertField(setEditedDesigners, rowKey);
    revertField(setEditedDesignerUnits, rowKey);
    revertField(setEditedDesignerWork, rowKey);
    revertField(setEditedQc2, rowKey);
    revertField(setEditedDesignTn, rowKey);
    revertField(setEditedDeliveryTn, rowKey);
    revertField(setEditedCategory, rowKey);
    revertField(setCategoryEdited, rowKey);
    revertField(setEditedFilesBy, rowKey);
    revertField(setEditedCrm, rowKey);
    revertField(setEditedModelType, rowKey);
    revertField(setEditedModelBy, rowKey);
    revertField(setEditedUnitsNotes, rowKey);
    revertField(setEditedIrAssignments, rowKey);
    revertField(setEditedRedesignCategory, rowKey);
    setEditingRows((prev) => {
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
  };

  const startEditing = (rowKey: string) => {
    Object.keys(editingRows).forEach((key) => {
      if (key !== rowKey) cancelEditing(key);
    });
    setEditingRows({ [rowKey]: true });
  };

  const reload = () =>
    isRecent
      ? dispatch(
          fetchRecentlyAssignedUnits({
            limit: RECENT_LIMIT,
            search,
          }),
        )
      : dispatch(
          fetchMarkedUnitsCases({
            page,
            limit: LIMIT,
            search,
            type: typeFilter,
          }),
        );

  useEffect(() => {
    dispatch(fetchAllEmployees());
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      if (!isRecent) setPage(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput, isRecent]);

  useEffect(() => {
    reload();
    resetEdits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, page, search, typeFilter, isRecent]);

  const handleTypeChange = (value: string) => {
    setTypeFilter(value as TypeFilter);
    setPage(1);
  };

  const handleSave = async () => {
    const updates = cases
      .filter((row: any) => editingRows[getRowKey(row)])
      .map((row: any) => {
        const key = getRowKey(row);
        const designer = editedDesigners[key] ?? row.designers;
        const designerNames = splitNames(designer);
        const designerUnits =
          editedDesignerUnits[key] ?? row.designer_units ?? {};
        const modelTypes = persistModelTypes(
          hasModelCategorySelection(
            editedModelType[key] ?? row.model_types ?? row.model_type,
          ),
          editedModelType[key] ?? row.model_types ?? row.model_type,
        );
        const hasModel = hasModelCategorySelection(modelTypes);
        const defaultModelUnits = modelDesignerUnits(hasModel, modelTypes);
        const hasModelWork = hasModel || hasModelWorkSelection(modelTypes);
        const extraCount = designerNames.slice(1).length;
        const primaryAuto = extraCount
          ? modelCategoryWorkUnits(modelTypes)
          : defaultModelUnits;
        const designerWork = parseDesignerWork(
          editedDesignerWork[key] ?? row.designer_work,
          designerNames,
          editedModelBy[key] ?? row.model_by,
          hasModelWork,
        );
        const modelBy = hasModelWork
          ? editedModelBy[key] ||
            row.model_by ||
            designerNames[0] ||
            ""
          : "";
        return {
          caseId: row.case_id,
          caseType: row.type as "live" | "redesign",
          newDeliveryUnits: editedDeliveryUnits[key] ?? row.delivery_units,
          designUnit: sumUnits(
            Object.fromEntries(
              Object.entries(designerUnits).filter(
                ([name]) => designerWork[name] !== "model",
              ),
            ),
          ),
          patientName:
            row.service_type === "filledOrder"
              ? row.details || row.patient_name || null
              : null,
          designer,
          captain: editedQc2[key] ?? row.qc2_users,
          designToothNumbers:
            editedDesignTn[key] ?? row.design_tooth_numbers ?? "",
          deliveryToothNumbers:
            editedDeliveryTn[key] ?? row.delivery_tooth_numbers ?? "",
          filesBy: editedFilesBy[key] ?? row.files_by ?? "",
          filesUnits: Number(row.files_units) || 0,
          model: hasModel ? "Yes" : "No",
          modelType: modelTypes.join(", "),
          modelTypes,
          modelBy,
          modelUnits: hasModelWork
            ? editedModelUnits[key] ?? primaryAuto
            : 0,
          designerUnits,
          designerWork,
          category: editedCategory[key] ?? row.category ?? "",
          crm: editedCrm[key] ?? row.crm ?? "",
          unitsNotes: editedUnitsNotes[key] ?? row.units_notes ?? "",
          ...(row.type === "redesign"
            ? {
                redesignCategory:
                  editedRedesignCategory[key] ?? row.redesign_category ?? "",
              }
            : {}),
          internalRedesigns: serializeInternalAssignments(
            editedIrAssignments[key] ??
              defaultInternalAssignments(row.internal_redesigns),
          ),
        };
      });

    if (!updates.length) return;

    const units_By =
      user && user?.role === "Super Admin"
        ? user.user_name
        : employee
          ? employee.emp_name
          : "System";

    try {
      setSaving(true);
      await dispatch(updateMarkedUnits({ units_By, cases: updates })).unwrap();
      resetEdits();
      await reload();
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const editingCount = Object.values(editingRows).filter(Boolean).length;
  const colCount = 12;
  const inputClass = unitsTableInputClass;
  const th = unitsTableTh;
  const td = unitsTableTd;

  if (user?.role !== "Super Admin" && employee?.role === "CRM") {
    return <Navigate to="/dashboard/profile" replace />;
  }

  return (
    <>
      <Helmet>
        <title>{isRecent ? "Last 50 Assigned" : "Marked Units"}</title>
      </Helmet>

      <AdminPageShell className="flex h-full min-h-0 flex-col gap-3 overflow-hidden text-[1.05rem]">
        <AdminPageHeader
          title={isRecent ? "Last 50 Assigned" : "Marked Units"}
          subtitle={
            isRecent
              ? "Most recently assigned cases. Click Edit to fix a mistake."
              : "Click Edit on a row to change it. Other rows stay read-only."
          }
        >
          <AdminSearchInput
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search by Case ID…"
          />
          {!isRecent ? (
            <AdminSelect value={typeFilter} onChange={handleTypeChange}>
              <option value="">All Types</option>
              <option value="live">Live</option>
              <option value="redesign">Redesign</option>
            </AdminSelect>
          ) : null}
          <a
            href="/dashboard/admin/units/ledger"
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border-2 border-white bg-transparent text-white hover:bg-white/20 text-xs font-medium transition"
          >
            Units Ledger
          </a>
          <button
            type="button"
            onClick={() => {
              reload();
              resetEdits();
            }}
            disabled={loading || saving}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border-2 border-white bg-transparent text-white hover:bg-white/20 text-xs font-medium transition disabled:opacity-50"
          >
            <FiRefreshCw
              size={14}
              className={loading ? "animate-spin" : ""}
            />
            Refresh
          </button>
          <AdminPrimaryButton
            onClick={handleSave}
            disabled={loading || saving || editingCount === 0}
            className="!border !border-white !bg-white !text-[#2fa9bf] hover:!bg-white/90 hover:!text-[#2296aa]"
          >
            <FiSave size={14} className={saving ? "animate-spin" : ""} />
            Save ({editingCount})
          </AdminPrimaryButton>
          {!isRecent ? (
            <AdminPagination
              tone="onAccent"
              page={currentPage}
              totalPages={totalPages}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => p + 1)}
              disabled={loading || saving}
            />
          ) : null}
        </AdminPageHeader>

        <AdminTableCard className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="min-w-full table-fixed border-separate border-spacing-0">
              <thead className="sticky top-0 z-20">
                <tr className="border-b border-[#E8E8E4]">
                  <th className={`${th} w-[8%]`}>Case</th>
                  <th className={`${th} w-[7%]`}>CRM</th>
                  <th className={`${th} w-[14%]`} title="Billing Category">
                    Bill. Cat.
                  </th>
                  <th className={`${th} w-[13%]`}>Designer</th>
                  <th className={`${th} w-[9%]`}>QC2</th>
                  <th className={`${th} w-[8%]`} title="Design tooth numbers">
                    Design TN
                  </th>
                  <th className={`${th} w-[8%]`} title="Delivery tooth numbers">
                    Delivery TN
                  </th>
                  <th className={`${th} w-[4%]`} title="Units">
                    Units
                  </th>
                  <th className={`${th} w-[9%]`} title="Files by">
                    Files
                  </th>
                  <th className={`${th} w-[9%]`}>Model</th>
                  <th className={`${th} w-[5%]`}>Notes</th>
                  <th className={`${th} w-[7%] text-center`}>Edit</th>
                </tr>
              </thead>

              <tbody>
                {cases.map((row: any, index: number) => {
                  const key = getRowKey(row);
                  const isEditing = Boolean(editingRows[key]);
                  const currentDesignTn =
                    editedDesignTn[key] ?? row.design_tooth_numbers ?? "";
                  const currentDeliveryTn =
                    editedDeliveryTn[key] ?? row.delivery_tooth_numbers ?? "";
                  const origModelTypes = parseModelTypes(
                    row.model_types ?? row.model_type,
                  );
                  const currModelTypeRows = parseModelTypes(
                    editedModelType[key] ?? origModelTypes,
                  );
                  const hasModel = hasModelCategorySelection(currModelTypeRows);
                  const origModel = hasModelCategorySelection(origModelTypes)
                    ? "Yes"
                    : "No";
                  const defaultModelUnits = modelDesignerUnits(
                    hasModel,
                    currModelTypeRows,
                  );
                  const categoryModelUnits = modelCategoryWorkUnits(
                    currModelTypeRows,
                  );
                  const otherModelUnits = modelOtherItemWorkUnits(
                    currModelTypeRows,
                  );
                  const extraModelCount = splitNames(
                    editedDesigners[key] ?? row.designers,
                  ).slice(1).length;
                  const primaryAutoModelUnits = extraModelCount
                    ? categoryModelUnits
                    : defaultModelUnits;
                  const hasModelWork =
                    hasModel || hasModelWorkSelection(currModelTypeRows);
                  const currModelUnits = hasModelWork
                    ? editedModelUnits[key] ??
                      (Number(row.model_units) || primaryAutoModelUnits)
                    : 0;
                  const jobCardNotes = String(
                    row.notes || row.job_card?.notes || "",
                  ).trim();
                  const jobCardModelNotes = String(
                    row.model_notes || row.job_card?.model_notes || "",
                  ).trim();
                  const origUnitsNotes = String(
                    row.units_notes || row.job_card?.units_notes || "",
                  ).trim();
                  const currUnitsNotes = editedUnitsNotes[key] ?? origUnitsNotes;
                  const hasJobCardNotes = Boolean(
                    jobCardNotes || (hasModel && jobCardModelNotes),
                  );

                  const currentCategory =
                    editedCategory[key] ?? row.category ?? "";
                  const origCrm = row.crm ?? "";
                  const currCrm = editedCrm[key] ?? origCrm;
                  const origCategory = row.category ?? "";
                  const origDesigner = row.designers ?? "";
                  const currDesigner = editedDesigners[key] ?? origDesigner;
                  const origDesignerUnits = row.designer_units ?? {};
                  const currDesignerUnits =
                    editedDesignerUnits[key] ?? origDesignerUnits;
                  const extraModelPeople = hasModelWork
                    ? splitNames(currDesigner).slice(1)
                    : [];
                  const origQc2 = splitNames(row.qc2_users)[0] || "";
                  const currQc2 =
                    splitNames(editedQc2[key] ?? row.qc2_users)[0] || "";
                  const origDesignTn = row.design_tooth_numbers ?? "";
                  const origDeliveryTn = row.delivery_tooth_numbers ?? "";
                  const origDeliveryUnits = row.delivery_units;
                  const currDeliveryUnits =
                    editedDeliveryUnits[key] ?? origDeliveryUnits;
                  const origFilesBy = row.files_by ?? "";
                  const currFilesBy = editedFilesBy[key] ?? origFilesBy;
                  const origModelBy =
                    origModel === "Yes" ||
                    hasModelWorkSelection(origModelTypes)
                      ? splitNames(row.model_by)[0] ||
                        splitNames(row.designers)[0] ||
                        ""
                      : "";
                  const currModelBy = editedModelBy[key] ?? origModelBy;
                  const currDesignerWork = parseDesignerWork(
                    editedDesignerWork[key] ?? row.designer_work,
                    splitNames(currDesigner),
                    currModelBy,
                    hasModelWork,
                  );
                  const currQcUnits = qcUnitsFromAssignedWork({
                    designerNames: splitNames(currDesigner),
                    designerUnits: currDesignerUnits,
                    designerWork: currDesignerWork,
                    hasModel: hasModelWork,
                    modelUnits: currModelUnits,
                  });
                  const qcCredit = qcCreditForPerson(
                    currQc2,
                    splitNames(currDesigner),
                    currDesignerWork,
                    currQcUnits,
                  );
                  const irItems = row.internal_redesigns || [];
                  const irAssignments =
                    editedIrAssignments[key] ??
                    defaultInternalAssignments(irItems);
                  const origRedesignCategory = row.redesign_category ?? "";
                  const currRedesignCategory =
                    editedRedesignCategory[key] ?? origRedesignCategory;
                  const rowKey = `${row.type}-${row.case_id}-${row.details || ""}-${index}`;

                  return (
                    <Fragment key={rowKey}>
                    <tr
                      className={`border-b border-[#E3E8E8] last:border-0 transition-colors ${categoryRowClass(
                        currentCategory,
                        index,
                        isEditing,
                      )} ${categoryAccent(currentCategory)}`}
                    >
                      <td className={`${td} break-words`}>
                        <a
                          href={(() => {
                            const isRedesign = /^RD-\d+-/.test(row.case_id);
                            const cleanCaseId = isRedesign
                              ? row.case_id.replace(/^RD-\d+-/, "")
                              : row.case_id;
                            return isRedesign
                              ? `/dashboard/admin/cases/${cleanCaseId}?tab=redesign`
                              : `/dashboard/admin/cases/${cleanCaseId}`;
                          })()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[1.2rem] font-semibold text-[#2fa9bf] hover:text-[#2296aa] hover:underline break-all leading-snug"
                        >
                          {row.case_id}
                        </a>
                        {row.type === "redesign" && isEditing ? (
                          <>
                            <RedesignScopeEditor
                              labels={currRedesignCategory}
                              onScopeChange={(scope: UnitsRedesignScope) => {
                                const next = applyClientRedesignScopeChange({
                                  currentLabels: currRedesignCategory,
                                  nextScope: scope,
                                  liveDeliveryUnits: row.live_delivery_units,
                                  currentDeliveryUnits:
                                    Number(currDeliveryUnits) || 0,
                                  designer: currDesigner,
                                  designerUnits: currDesignerUnits,
                                });
                                setEditedRedesignCategory((prev) => ({
                                  ...prev,
                                  [key]: next.nextLabels,
                                }));
                                if (next.nextDeliveryUnits != null) {
                                  setEditedDeliveryUnits((prev) => ({
                                    ...prev,
                                    [key]: next.nextDeliveryUnits as number,
                                  }));
                                }
                                if (next.nextDesignerUnits) {
                                  setEditedDesignerUnits((prev) => ({
                                    ...prev,
                                    [key]: next.nextDesignerUnits as Record<
                                      string,
                                      number
                                    >,
                                  }));
                                }
                              }}
                            />
                            <OriginalValue
                              original={
                                parseUnitsRedesignLabels(origRedesignCategory)
                                  .scope
                              }
                              current={
                                parseUnitsRedesignLabels(currRedesignCategory)
                                  .scope
                              }
                              onRestore={() =>
                                revertField(setEditedRedesignCategory, key)
                              }
                            />
                          </>
                        ) : (
                          <RedesignScopeBadges labels={currRedesignCategory} />
                        )}
                        {row.type === "redesign" ? (
                          <LiveCaseUnitsHint
                            liveDeliveryUnits={row.live_delivery_units}
                            liveUnitsBy={row.live_units_By}
                          />
                        ) : null}
                        {irItems.length ? (
                          <div className="mt-1 inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-semibold text-violet-900">
                            {irItems.length} internal redesign
                            {irItems.length === 1 ? "" : "s"}
                          </div>
                        ) : (
                          <div className="mt-1 text-[11px] text-[#8A8A85]">
                            No internal redesigns
                          </div>
                        )}
                        {row.service_type === "filledOrder" &&
                        (row.patient_name || row.details) ? (
                          <div className="mt-1 text-[11px] font-medium text-[#6B6B66] whitespace-normal">
                            {row.patient_name || row.details}
                          </div>
                        ) : null}
                        {row.units_By ? (
                          <div className="mt-1 text-[12px] text-[#8A8A85] whitespace-normal">
                            Marked by {row.units_By}
                          </div>
                        ) : null}
                      </td>

                      <td className={td}>
                        {isEditing ? (
                          <>
                            <div>
                              <EmployeeNameEditor
                                value={currCrm}
                                options={crmList}
                                allowMultiple={false}
                                allowEmpty
                                placeholder="CRM"
                                onChange={(value) => {
                                  setEditedCrm((prev) => ({
                                    ...prev,
                                    [key]: value,
                                  }));
                                }}
                              />
                            </div>
                            <OriginalValue
                              original={origCrm}
                              current={currCrm}
                              onRestore={() => revertField(setEditedCrm, key)}
                            />
                          </>
                        ) : (
                          <ReadValue>{origCrm}</ReadValue>
                        )}
                      </td>

                      <td className={`${td}`}>
                        {isEditing ? (
                          <>
                            <div>
                              <WrappingSelect
                                value={currentCategory}
                                onChange={(value) => {
                                  setEditedCategory((prev) => ({
                                    ...prev,
                                    [key]: value,
                                  }));
                                  setCategoryEdited((prev) => ({
                                    ...prev,
                                    [key]: true,
                                  }));
                                }}
                                placeholder="Select"
                                title={currentCategory}
                              >
                                <option value="">Select</option>
                                {BILLING_CATEGORIES.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </WrappingSelect>
                            </div>
                            <OriginalValue
                              original={origCategory}
                              current={currentCategory}
                              onRestore={() => {
                                revertField(setEditedCategory, key);
                                revertField(setCategoryEdited, key);
                              }}
                            />
                          </>
                        ) : (
                          <ReadValue>{origCategory}</ReadValue>
                        )}
                      </td>

                      <td className={`${td}`}>
                        {isEditing ? (
                          <>
                            <div>
                              <EmployeeNameEditor
                                value={currDesigner}
                                options={designersList}
                                placeholder="Designer"
                                showUnits
                                unitsByName={currDesignerUnits}
                                showModelPerson={hasModelWork}
                                modelBy={currModelBy}
                                modelUnits={currModelUnits}
                                autoModelUnits={primaryAutoModelUnits}
                                autoOtherModelUnits={otherModelUnits}
                                modelNotesHint={
                                  [jobCardNotes, jobCardModelNotes]
                                    .filter(Boolean)
                                    .join(" ")
                                }
                                onModelByChange={(name) => {
                                  setEditedModelBy((prev) => ({
                                    ...prev,
                                    [key]: name,
                                  }));
                                }}
                                onModelUnitsChange={(units) => {
                                  setEditedModelUnits((prev) => ({
                                    ...prev,
                                    [key]: Math.max(units, extraModelCount ? 0 : 1),
                                  }));
                                }}
                                onWorkChange={(work) => {
                                  setEditedDesignerWork((prev) => ({
                                    ...prev,
                                    [key]: work,
                                  }));
                                }}
                                onChange={(value) => {
                                  setEditedDesigners((prev) => ({
                                    ...prev,
                                    [key]: value,
                                  }));
                                }}
                                onUnitsChange={(units) => {
                                  setEditedDesignerUnits((prev) => ({
                                    ...prev,
                                    [key]: units,
                                  }));
                                }}
                              />
                            </div>
                            <OriginalValue
                              original={formatPeopleUnits(
                                origDesigner,
                                origDesignerUnits,
                              )}
                              current={formatPeopleUnits(
                                currDesigner,
                                currDesignerUnits,
                              )}
                              onRestore={() => {
                                revertField(setEditedDesigners, key);
                                revertField(setEditedDesignerUnits, key);
                                revertField(setEditedDesignerWork, key);
                                revertField(setEditedModelBy, key);
                                revertField(setEditedModelUnits, key);
                              }}
                            />
                          </>
                        ) : (
                          <ReadValue>
                            {formatPeopleUnits(origDesigner, origDesignerUnits)}
                            {hasModel
                              ? extraModelPeople.length
                                ? extraModelPeople.map((name) => (
                                    <div className="mt-1" key={name}>
                                      <ModelPersonRow
                                        name={name}
                                        options={[]}
                                        units={Math.max(
                                          Number(currDesignerUnits[name]) || 0,
                                          1,
                                        )}
                                      />
                                    </div>
                                  ))
                                : currModelBy
                                  ? (
                                      <div className="mt-1">
                                        <ModelPersonRow
                                          name={currModelBy}
                                          options={[]}
                                          units={currModelUnits}
                                        />
                                      </div>
                                    )
                                  : null
                              : null}
                          </ReadValue>
                        )}
                      </td>

                      <td className={td}>
                        {isEditing ? (
                          <>
                            <div>
                              <EmployeeNameEditor
                                value={currQc2}
                                options={qc2List}
                                allowMultiple={false}
                                placeholder="QC2"
                                onChange={(value) => {
                                  setEditedQc2((prev) => ({
                                    ...prev,
                                    [key]: value,
                                  }));
                                }}
                              />
                            </div>
                            <div className="mt-1 text-[12px] font-medium text-[#4A4A48]">
                              {qcCredit.units} units
                              <span className="ml-1 font-normal text-[#8A8A85]">
                                {qcCredit.samePerson
                                  ? "QC2 recorded, design units only"
                                  : "(design + model)"}
                              </span>
                            </div>
                            <OriginalValue
                              original={origQc2}
                              current={currQc2}
                              onRestore={() => revertField(setEditedQc2, key)}
                            />
                          </>
                        ) : (
                          <ReadValue>
                            {origQc2}
                            {origQc2 ? (
                              <div className="mt-1 text-[12px] font-medium text-[#4A4A48]">
                                {qcCredit.units} units
                                <span className="ml-1 font-normal text-[#8A8A85]">
                                  {qcCredit.samePerson
                                    ? "QC2 recorded, design units only"
                                    : "(design + model)"}
                                </span>
                              </div>
                            ) : null}
                          </ReadValue>
                        )}
                      </td>

                      <td className={td}>
                        {isEditing ? (
                          <>
                            <ToothNumberPicker
                              compact
                              label="Tooth no. + Enter"
                              value={currentDesignTn}
                              onChange={(value) => {
                                setEditedDesignTn((prev) => ({
                                  ...prev,
                                  [key]: value,
                                }));
                              }}
                            />
                            <OriginalValue
                              original={origDesignTn}
                              current={currentDesignTn}
                              onRestore={() => revertField(setEditedDesignTn, key)}
                            />
                          </>
                        ) : (
                          <div className="px-1 py-1">
                            <ToothChips value={origDesignTn} />
                          </div>
                        )}
                      </td>

                      <td className={td}>
                        {isEditing ? (
                          <>
                            <ToothNumberPicker
                              compact
                              label="Tooth no. + Enter"
                              value={currentDeliveryTn}
                              onChange={(value) => {
                                setEditedDeliveryTn((prev) => ({
                                  ...prev,
                                  [key]: value,
                                }));
                              }}
                            />
                            <OriginalValue
                              original={origDeliveryTn}
                              current={currentDeliveryTn}
                              onRestore={() =>
                                revertField(setEditedDeliveryTn, key)
                              }
                            />
                          </>
                        ) : (
                          <div className="px-1 py-1">
                            <ToothChips value={origDeliveryTn} />
                          </div>
                        )}
                      </td>

                      <td className={td}>
                        {isEditing ? (
                          <>
                            <input
                              type="number"
                              min={0}
                              value={currDeliveryUnits}
                              onChange={(e) => {
                                const nextUnits = Number(e.target.value);
                                const prevUnits = currDeliveryUnits;
                                const prevSuggested = suggestBillingCategory({
                                  serviceType: row.service_type || row.treatments,
                                  units: Number(prevUnits) || 0,
                                  model: hasModel,
                                });
                                setEditedDeliveryUnits((prev) => ({
                                  ...prev,
                                  [key]: nextUnits,
                                }));
                                if (
                                  !categoryEdited[key] &&
                                  (!currentCategory ||
                                    currentCategory === prevSuggested)
                                ) {
                                  setEditedCategory((prev) => ({
                                    ...prev,
                                    [key]: suggestBillingCategory({
                                      serviceType:
                                        row.service_type || row.treatments,
                                      units: nextUnits,
                                      model: hasModel,
                                    }),
                                  }));
                                }
                              }}
                              className={inputClass}
                            />
                            <OriginalValue
                              original={origDeliveryUnits}
                              current={currDeliveryUnits}
                              onRestore={() =>
                                revertField(setEditedDeliveryUnits, key)
                              }
                            />
                          </>
                        ) : (
                          <ReadValue>{origDeliveryUnits}</ReadValue>
                        )}
                      </td>

                      <td className={td}>
                        {isEditing ? (
                          <>
                            <div>
                              <EmployeeNameEditor
                                value={currFilesBy}
                                options={designersList}
                                allowMultiple={false}
                                allowEmpty
                                placeholder="Files by"
                                onChange={(value) => {
                                  setEditedFilesBy((prev) => ({
                                    ...prev,
                                    [key]: value,
                                  }));
                                }}
                              />
                            </div>
                            <OriginalValue
                              original={origFilesBy}
                              current={currFilesBy}
                              onRestore={() => revertField(setEditedFilesBy, key)}
                            />
                          </>
                        ) : (
                          <ReadValue>{origFilesBy}</ReadValue>
                        )}
                      </td>

                      <td className={td}>
                        {isEditing ? (
                          <>
                            <div>
                              <UnitsModelEditor
                                value={{
                                  model: hasModel ? "Yes" : "No",
                                  modelTypes: currModelTypeRows,
                                  modelBy: currModelBy,
                                }}
                                onChange={(next) => {
                                  const nextHasWork =
                                    next.model === "Yes" ||
                                    hasModelWorkSelection(next.modelTypes);
                                  setEditedModelType((prev) => ({
                                    ...prev,
                                    [key]: next.modelTypes,
                                  }));
                                  setEditedModelUnits((prev) => {
                                    const current = prev[key];
                                    if (
                                      current != null &&
                                      current !== primaryAutoModelUnits
                                    ) {
                                      if (nextHasWork) return prev;
                                      const nextUnits = { ...prev };
                                      delete nextUnits[key];
                                      return nextUnits;
                                    }
                                    if (!nextHasWork || key in prev) {
                                      if (!(key in prev)) return prev;
                                      const nextUnits = { ...prev };
                                      delete nextUnits[key];
                                      return nextUnits;
                                    }
                                    return prev;
                                  });
                                  if (!nextHasWork) {
                                    setEditedModelBy((prev) => ({
                                      ...prev,
                                      [key]: "",
                                    }));
                                  } else if (!currModelBy) {
                                    const fallback =
                                      splitNames(currDesigner)[0] || "";
                                    if (fallback) {
                                      setEditedModelBy((prev) => ({
                                        ...prev,
                                        [key]: fallback,
                                      }));
                                    }
                                  }
                                }}
                              />
                            </div>
                            <OriginalValue
                              original={formatModelTypes(origModelTypes)}
                              current={formatModelTypes(currModelTypeRows)}
                              onRestore={() => {
                                revertField(setEditedModelType, key);
                                revertField(setEditedModelBy, key);
                                revertField(setEditedModelUnits, key);
                              }}
                            />
                          </>
                        ) : (
                          <UnitsModelEditor
                            value={{
                              model: origModel,
                              modelTypes: origModelTypes,
                              modelBy: origModelBy,
                            }}
                          />
                        )}
                      </td>

                      <td className={`${td}`}>
                        <button
                          type="button"
                          onClick={() =>
                            setNotesModal({
                              caseId: row.case_id,
                              rowKey: key,
                              notes: jobCardNotes,
                              modelNotes: hasModel ? jobCardModelNotes : "",
                              category: currentCategory,
                              unitsNotes: currUnitsNotes,
                              unitsNotesEditable: true,
                            })
                          }
                          className={`rounded-lg px-2.5 py-1 text-[14px] font-medium ${
                            hasJobCardNotes || currUnitsNotes
                              ? "bg-[#2fa9bf] text-white hover:bg-[#2296aa]"
                              : "border border-[#C9D4D6] bg-white text-[#4A4A48] hover:border-[#2fa9bf] hover:text-[#2fa9bf]"
                          }`}
                        >
                          Notes
                        </button>
                      </td>

                      <td className={`${td} text-center`}>
                        {isEditing ? (
                          <button
                            type="button"
                            onClick={() => cancelEditing(key)}
                            className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] font-medium text-rose-700 hover:bg-rose-100"
                          >
                            <FiX size={12} />
                            Cancel
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => startEditing(key)}
                            className="inline-flex items-center gap-1 rounded-lg border border-[#C9D4D6] bg-white px-2 py-1 text-[11px] font-medium text-[#4A4A48] hover:bg-[#F3FBFC] hover:border-[#2fa9bf] hover:text-[#2fa9bf]"
                          >
                            <FiEdit2 size={12} />
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                    {irItems.length ? (
                      <tr className="border-b border-[#E3E8E8]">
                        <td colSpan={colCount} className="px-2 pb-3 pt-0">
                          <InternalRedesignsUnitsPanel
                            items={irItems}
                            assignments={irAssignments}
                            parentDesigner={currDesigner}
                            parentQc2={currQc2}
                            designerOptions={designersList}
                            qc2Options={qc2List}
                            editable={isEditing}
                            onAssignmentsChange={(next) => {
                              if (!isEditing) startEditing(key);
                              setEditedIrAssignments((prev) => ({
                                ...prev,
                                [key]: next,
                              }));
                            }}
                          />
                        </td>
                      </tr>
                    ) : null}
                    </Fragment>
                  );
                })}

                {!loading && cases.length === 0 ? (
                  <AdminTableState
                    colSpan={colCount}
                    variant="empty"
                    message={
                      isRecent
                        ? "No recently assigned cases found"
                        : "No marked cases found"
                    }
                  />
                ) : null}

                {loading ? (
                  <AdminTableState
                    colSpan={colCount}
                    variant="loading"
                    message="Loading…"
                  />
                ) : null}
              </tbody>
            </table>
          </div>
        </AdminTableCard>
      </AdminPageShell>

      {notesModal ? (
        <JobCardNotesModal
          notesModal={notesModal}
          onClose={() => setNotesModal(null)}
          onUnitsNotesChange={(value) => {
            const key = notesModal.rowKey;
            if (!editingRows[key]) startEditing(key);
            setEditedUnitsNotes((prev) => ({ ...prev, [key]: value }));
            setNotesModal((prev) =>
              prev ? { ...prev, unitsNotes: value } : prev,
            );
          }}
        />
      ) : null}
    </>
  );
};

const MarkedUnits = () => <MarkedUnitsPage mode="marked" />;

export default MarkedUnits;